import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useFetch } from '@reveni-io/reveni-ui'

import useStoreProductCategories from '@/common/hooks/useStoreProductCategories/useStoreProductCategories'
import { widgetDefault } from '@/configuration/pages/ConfigurationPage/ExtrasConfiguration/SizingWidgetConfigurationPage/constants'
import { productTaxonomyDisplayOptions } from '@/configuration/pages/ConfigurationPage/ExtrasConfiguration/SizingWidgetConfigurationPage/SizingWidgetConfiguration/ProductOptions/constants'
import { getTranslations } from '@/configuration/pages/ConfigurationPage/helpers'
import configurationServices from '@/configuration/services/services'

import { getEditPreferencesPayload } from './helpers'

const useSizingWidgetConfiguration = ({ storeId, setToastInfo, supportedLanguages }) => {
  const { t } = useTranslation()
  const { data, fetchData, loading } = useFetch(() => configurationServices.getSizingWidgetPreferences({ storeId }), {
    doInitialRequest: true,
  })
  const { productCategories, fetchingProductCategories } = useStoreProductCategories({
    storeId,
    doInitialRequest: true,
  })
  const { fetchData: editWidgetPreferences, loading: editing } = useFetch(
    configurationServices.editSizingWidgetPreferences,
    {
      doInitialRequest: false,
    }
  )
  const [backgroundColor, setBackgroundColor] = useState(null)
  const [textColor, setTextColor] = useState(null)
  const [useCustomTextTooBig, setUseCustomTextTooBig] = useState(null)
  const [useCustomTextTooSmall, setUseCustomTextTooSmall] = useState(null)
  const [customTextTooBig, setCustomTextTooBig] = useState(null)
  const [customTextTooSmall, setCustomTextTooSmall] = useState(null)
  const [productTaxonomyDisplay, setProductTaxonomyDisplay] = useState(productTaxonomyDisplayOptions.all)
  const [productTaxonomyIds, setProductTaxonomyIds] = useState([])
  const [hasChanges, setHasChanges] = useState(false)

  const setInitValues = useCallback(() => {
    if (data) {
      setBackgroundColor(data?.background_color || widgetDefault.background)
      setTextColor(data?.text_color || widgetDefault.text)
      setCustomTextTooBig(getTranslations(supportedLanguages, data?.too_big_text))
      setCustomTextTooSmall(getTranslations(supportedLanguages, data?.too_small_text))
      setUseCustomTextTooBig(Object.values(data?.too_big_text).some(value => value !== null))
      setUseCustomTextTooSmall(Object.values(data?.too_small_text).some(value => value !== null))
      setProductTaxonomyDisplay(data?.product_taxonomy_display || productTaxonomyDisplayOptions.all)
      setProductTaxonomyIds(data?.product_taxonomy_ids || [])
      setHasChanges(false)
    }
  }, [data, supportedLanguages])

  useEffect(() => {
    setInitValues()
  }, [setInitValues])

  const revertChanges = () => {
    setInitValues()
  }

  const getScriptExample = () => {
    return `<script defer="defer" type="text/javascript" src='${import.meta.env.VITE_REVENI_SIZING_WIDGET}?platform=shopify&language={{request.locale.iso_code}}&sizeSuggestion={{product.metafields.reveni.recommendation_regions}}&country={{localization.country.iso_code}}&configuration={{ shop.metafields.reveni.size_widget_recommendation.value | json }}'></script>`
  }

  const onSave = async () => {
    try {
      const preferencesPayload = getEditPreferencesPayload({
        backgroundColor,
        textColor,
        useCustomTextTooBig,
        useCustomTextTooSmall,
        customTextTooBig,
        customTextTooSmall,
        supportedLanguages,
        productTaxonomyDisplay,
        productTaxonomyIds,
      })

      await editWidgetPreferences({ storeId, preferences: preferencesPayload })
      setHasChanges(false)
      setToastInfo({ show: true, type: 'success', message: t('Cambios guardados') })
    } catch (error) {
      setToastInfo({ show: true, type: 'error', message: error.errorMessage })
    }
  }

  return {
    sizingWidgetPreferences: data,
    fetchingSizingWidgetPreferences: loading || fetchingProductCategories,
    hasChanges,
    refetchSizingWidgetPreferences: fetchData,
    backgroundColor,
    setBackgroundColor: param => {
      setHasChanges(true)
      setBackgroundColor(param)
    },
    textColor,
    setTextColor: param => {
      setHasChanges(true)
      setTextColor(param)
    },
    useCustomTextTooBig,
    setUseCustomTextTooBig: param => {
      setHasChanges(true)
      setUseCustomTextTooBig(param)
    },
    useCustomTextTooSmall,
    setUseCustomTextTooSmall: param => {
      setHasChanges(true)
      setUseCustomTextTooSmall(param)
    },
    customTextTooBig,
    setCustomTextTooBig: params => {
      setHasChanges(true)
      setCustomTextTooBig(params)
    },
    customTextTooSmall,
    setCustomTextTooSmall: params => {
      setHasChanges(true)
      setCustomTextTooSmall(params)
    },
    productTaxonomyDisplay,
    setProductTaxonomyDisplay: param => {
      setHasChanges(true)
      setProductTaxonomyDisplay(param)
    },
    productTaxonomyIds,
    setProductTaxonomyIds: param => {
      setHasChanges(true)
      setProductTaxonomyIds(param)
    },
    productCategories,
    revertChanges,
    scriptExample: getScriptExample(),
    onSave,
    editing,
  }
}

export default useSizingWidgetConfiguration
